import React from 'react';
import FeatureTile from '../FeatureTile';
import { useTileData } from '../../../hooks/useTileData';
import { getInteractionCounts } from '../../../api';
import { fmtNumber } from '../../shared/format';
import { useActiveRole } from '../../../hooks/useActiveRole';

// Kind is always 'ai_agent' — the chat/voice split lives on `modality`. The
// aggregate RPC returns per-modality counts; extract our slice from by_modality.
const extractCount = (data, wanted) => {
  const row = (data?.by_modality || []).find((r) => r.modality === wanted);
  return Number(row?.count ?? 0);
};

const Icon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

export default function ChatTile({ clientId, rangeStart, rangeEnd }) {
  const { data, loading, error } = useTileData(
    async () => {
      const resp = await getInteractionCounts(clientId, rangeStart, rangeEnd);
      return resp?.data ?? null;
    },
    [clientId, rangeStart, rangeEnd],
    Boolean(clientId && rangeStart && rangeEnd),
  );

  const total = Number(data?.total ?? 0);
  const chatCount = extractCount(data, 'chat');
  const share = total > 0 ? (chatCount / total) * 100 : 0;
  const avgQuality = data?.avg_quality_score;

  const primary = data ? {
    value: fmtNumber(chatCount),
    label: 'Conversations',
    subtitle: total > 0 ? `${share.toFixed(0)}% of all interactions` : 'This period',
  } : null;
  // avg_quality_score is aggregated across ALL conversations, not per-modality.
  // Directionally useful; upgrade when a per-modality RPC lands.
  const secondary = data ? [
    { value: avgQuality != null ? Number(avgQuality).toFixed(1) : '—', label: 'Avg quality' },
    { value: fmtNumber(data.has_booking ?? 0), label: 'With booking' },
  ] : [];

  // 2026-07-28 — CTA route matches NavSidebarGrouped's chat/voice routing:
  // admin lands on ProjectDetailPage (/client/:id), client on
  // ClientProjectDetailPage (/client-projects/:id). Both filter the
  // transcript list by ?modality=chat. Hard-coded /chat used to 404.
  const role = useActiveRole().role;
  const isClient = role === 3;
  const ctaTo = clientId
    ? `${isClient ? '/client-projects' : '/client'}/${clientId}?modality=chat`
    : (isClient ? '/client-dashboard' : '/dashboard');

  return (
    <FeatureTile
      title="AI Chat"
      icon={<Icon />}
      color="teal"
      primary={primary}
      secondary={secondary}
      ctaLabel="Open"
      ctaTo={ctaTo}
      loading={loading}
      error={error}
    />
  );
}
